import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  MessageCircle, Send, User, Bot, Loader2, X,
  ChevronLeft, Search, Clock, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { API, useAuth } from '../App';

const ChatDropdown = () => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const messagesEndRef = useRef(null);

  const orgId = user?.org_id;

  useEffect(() => {
    if (open && token) {
      fetchConversations();
    }
  }, [open, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/admin/conversations`, { headers });
      setConversations(res.data);
      
      // Calculate total unread
      const totalUnread = res.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadTotal(totalUnread);
    } catch (err) {
      console.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setMessagesLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/conversations/${conv.conversation_id}`, { headers });
      setMessages(res.data.messages || []);
      
      // Mark as read
      if (conv.unread_count > 0) {
        await axios.put(`${API}/support/admin/conversations/${conv.conversation_id}/read`, {}, { headers });
        setConversations(prev => prev.map(c => 
          c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c
        ));
        setUnreadTotal(prev => Math.max(0, prev - conv.unread_count));
      }
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/support/admin/conversations/${selectedConversation.conversation_id}/reply`,
        { content: replyText },
        { headers }
      );
      
      setMessages(prev => [...prev, res.data]);
      setReplyText('');
      
      // Refresh conversation list to update timestamps
      fetchConversations();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    setSelectedConversation(null);
    setMessages([]);
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.user_name?.toLowerCase().includes(query) ||
      conv.user_email?.toLowerCase().includes(query)
    );
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="w-3 h-3 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="w-3 h-3 text-green-500" />;
      default:
        return null;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-2xl hover:bg-slate-100 transition-colors"
          data-testid="chat-dropdown-trigger"
        >
          <MessageCircle className="w-5 h-5 text-slate-600" />
          {unreadTotal > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-rose-500 text-white rounded-full flex items-center justify-center">
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-96 p-0 rounded-2xl overflow-hidden"
        sideOffset={8}
      >
        {!selectedConversation ? (
          // Conversation List View
          <div className="flex flex-col max-h-[480px]">
            {/* Header */}
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Messages
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchConversations}
                  className="h-7 w-7 p-0 rounded-xl"
                >
                  <Loader2 className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-8 h-8 text-sm rounded-xl border-slate-200"
                />
              </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No conversations</p>
                </div>
              ) : (
                filteredConversations.slice(0, 10).map((conv) => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => selectConversation(conv)}
                    className="w-full p-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900 text-sm truncate">
                            {conv.user_name || 'Guest'}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {conv.unread_count > 0 && (
                              <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                {conv.unread_count}
                              </span>
                            )}
                            {getStatusIcon(conv.status)}
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          {formatDistanceToNow(new Date(conv.updated_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            {/* Footer */}
            <div className="p-2 border-t border-slate-100 bg-slate-50">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-slate-600 text-xs h-8 rounded-xl"
                onClick={() => {
                  setOpen(false);
                  window.location.href = '/admin/support';
                }}
              >
                View all conversations
              </Button>
            </div>
          </div>
        ) : (
          // Message View
          <div className="flex flex-col h-[480px]">
            {/* Header */}
            <div className="p-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="h-7 w-7 p-0 rounded-xl"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-900 text-sm truncate">
                  {selectedConversation.user_name || 'Guest'}
                </h3>
                {selectedConversation.user_email && (
                  <p className="text-xs text-slate-400 truncate">{selectedConversation.user_email}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50/50">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
                </div>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.message_id}
                    className={`flex gap-2 ${msg.role === 'user' ? '' : 'flex-row-reverse'}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      msg.role === 'user' ? 'bg-slate-200' :
                      msg.role === 'assistant' ? 'bg-blue-500' : 'bg-emerald-500'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <Bot className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                      msg.role === 'user' 
                        ? 'bg-white border border-slate-200 rounded-tl-sm' 
                        : msg.role === 'assistant'
                        ? 'bg-blue-500 text-white rounded-tr-sm'
                        : 'bg-emerald-500 text-white rounded-tr-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-slate-400' : 'opacity-70'}`}>
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-3 border-t border-slate-100 bg-white">
              <div className="flex gap-2">
                <Input
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                  placeholder="Type a reply..."
                  className="flex-1 h-9 text-sm rounded-xl border-slate-200"
                  disabled={sending}
                />
                <Button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  size="sm"
                  className="h-9 w-9 p-0 rounded-xl bg-slate-900"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ChatDropdown;
